import React, { useEffect, useRef, useState } from "react";
import { Alert, Box, ClickAwayListener, Snackbar } from "@mui/material";
import { useParams } from "react-router";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "@store/store.tsx";
import { SignalRContext } from "@config/signalR.ts";
import { addMessageToTop, clearTicketCreatedError } from "@store/chatSlice.tsx";
import InputField from "./inputField";
import DrawerActions from "./drawerActions";
import FilesSelectedCard from "./filesSelectedCard";

interface ChatInputSectionProps {
  setAddUserModal: React.Dispatch<React.SetStateAction<boolean>>;
  customStatus: string,
}

const ChatInputSection: React.FC<ChatInputSectionProps> = ({
  setAddUserModal,
  customStatus
}) => {
  const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);
  const mediaInputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dispatch = useDispatch<AppDispatch>();
  const { id } = useParams();
  const topMessageRef = useRef<HTMLDivElement | null>(null);
  const attachments = useSelector((state: any) => state.chat.attachments);
  const {createTicketError} = useSelector((state: any) => state.chat);
  const { wsConnected } = useSelector((state: any) => state.config);
  const [fileError, setFileError] = useState(null);

  useEffect(() => {
    if (id && id != "new" && wsConnected) {
      SignalRContext.invoke("JoinGroup", id.toString())
        ?.then((resp) => {
          console.log(resp, "joinGroup");
        })
        .catch((e) => console.log(e));
    }
    return () => {
      if (id && id != "new") {
        SignalRContext.invoke("LeaveGroup", id.toString())
          ?.then((resp) => {
            console.log(resp, "leaveGroup");
          })
          .catch((e) => console.log(e));
      }
    };
  }, [id, wsConnected]);

  SignalRContext.useSignalREffect(
    "ReceiveMessage",
    (message) => {
      dispatch(addMessageToTop(message));
      if (topMessageRef.current) {
        topMessageRef.current.scrollTo({ bottom: 0, behavior: "smooth" });
      }
    },
    [id, wsConnected]
  );

  const handleShareAction = () => {
    setAddUserModal(true);
  };

  const handleAlertClose = () => {
    setFileError(null);
    dispatch(clearTicketCreatedError());
  };

  return (
    <>
      <ClickAwayListener onClickAway={() => setIsDrawerOpen(false)}>
        <Box className="w-full">
          <Box className="flex flex-col mb-auto relative">
            {isDrawerOpen && (
              <Box
                className="hidden md:grid grid-cols-3 gap-5 w-fit absolute bottom-0"
                sx={{
                  marginBottom: "0.2rem",
                  backgroundColor: "rgba(224, 244, 255, 1)",
                  overflowY: "auto",
                  borderRadius: 2,
                  padding: 1,
                  paddingTop: 2,
                  zIndex: 1,
                }}
              >
                <DrawerActions
                  mediaInputRef={mediaInputRef}
                  fileInputRef={fileInputRef}
                  handleShareAction={handleShareAction}
                  setIsDrawerOpen={setIsDrawerOpen}
                  setFileError={setFileError}
                />
              </Box>
            )}

            {attachments?.length > 0 && <FilesSelectedCard />}
          </Box>

          <Box className={isDrawerOpen ? "pb-0 h-[28%] md:h-auto " : "pb-0 "}>
            <InputField
              isDrawerOpen={isDrawerOpen}
              setIsDrawerOpen={setIsDrawerOpen}
              customStatus={customStatus}
              setFileError={setFileError}
            />
            {isDrawerOpen && (
              <Box
                className="grid grid-cols-3 gap-3 hidden-scrollbar md:hidden "
                sx={{
                  backgroundColor: "rgba(224, 244, 255, 1)",
                  paddingTop: 2,
                }}
              >
                <DrawerActions
                  mediaInputRef={mediaInputRef}
                  fileInputRef={fileInputRef}
                  handleShareAction={handleShareAction}
                  setIsDrawerOpen={setIsDrawerOpen}
                  setFileError={setFileError}
                />
              </Box>
            )}
          </Box>
        </Box>
      </ClickAwayListener>
      <Snackbar
        open={Boolean(fileError) || createTicketError}
        autoHideDuration={5000}
        onClose={handleAlertClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert variant="filled" severity="error">
          {fileError ?? createTicketError}
        </Alert>
      </Snackbar>
    </>
  );
};
export default ChatInputSection;
